import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Sparkles } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { ROLES } from '@/shared/session/roleStore';
import { useActingRole } from '@/shared/session/useActingRole';
import { useSession } from '@/shared/session/useSession';
import type { PromptCategory } from '../model/prompts';
import { riseSx } from './motion';
import { NewResearch } from './NewResearch';
import { PromptTabs } from './PromptTabs';

type Props = {
  composer: ReactNode;
  /** A prompt goes into the box to edit. */
  onPrompt: (q: string) => void;
  onSeeAll: (category: PromptCategory) => void;
};

const greeting = (hour: number) => (hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening');

/** The home page before a chat starts: a greeting, the question box, prompts by category and the newest research. */
export function ChatWelcome({ composer, onPrompt, onSeeAll }: Props) {
  const { role } = useActingRole();
  const name = useSession().data?.name ?? ROLES[role].user;
  const [hour] = useState(() => new Date().getHours());

  return (
    <Stack sx={{ gap: 3, width: '100%' }}>
      <Stack sx={{ alignItems: 'center', gap: 1.25, textAlign: 'center', ...riseSx }}>
        <Box aria-hidden sx={markSx}>
          <Sparkles size={20} strokeWidth={1.75} />
        </Box>
        <Typography variant="h1" component="h1" sx={{ mt: 1.5 }}>
          {greeting(hour)}, {name.split(' ')[0]}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 400, fontSize: '0.875rem' }}>
          Ask about OCBC forecasts, drivers and reports. Every answer cites the report, date and page.
        </Typography>
      </Stack>
      <Box sx={{ ...riseSx, animationDelay: '60ms' }}>{composer}</Box>
      <Box sx={{ ...riseSx, animationDelay: '120ms' }}>
        <PromptTabs onPick={onPrompt} onSeeAll={onSeeAll} />
      </Box>
      <Box sx={{ ...riseSx, animationDelay: '180ms' }}>
        <NewResearch />
      </Box>
    </Stack>
  );
}

const markSx = {
  width: 40,
  height: 40,
  borderRadius: 1.5,
  display: 'grid',
  placeItems: 'center',
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  boxShadow: '0 4px 12px color-mix(in srgb, var(--mui-palette-primary-main) 30%, transparent)',
} as const;
